import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getCardDisclaimerKey } from "@/features/card/utils/card-disclaimer";
import {
  findPaymentMethodForCard,
  useAvailablePaymentMethods,
} from "@/features/payment-methods";
import { type PaymentAccountCard } from "@/features/payments/types/payment-account-cards";

import { useCardClientName } from "./use-card-client-name";

/**
 * Translated issuer disclosure for a card, or `null` when none applies. The
 * rules live in `getCardDisclaimerKey`; this hook only gathers the facts they
 * need and translates the result.
 */
export const useCardDisclaimer = (card: PaymentAccountCard): string | null => {
  const { t } = useTranslation();
  const clientName = useCardClientName();
  const { availablePaymentMethods, isLoadingAvailablePaymentMethods } =
    useAvailablePaymentMethods();

  const issuingMethod = findPaymentMethodForCard(
    availablePaymentMethods,
    card.card_type,
    card.provider
  );
  const disclaimerKey = getCardDisclaimerKey({
    provider: card.provider,
    sourcePlatform: issuingMethod?.metadata?.source_platform,
  });
  // Only worth flagging once the methods have loaded, since a Stripe card's
  // platform is unknown until then.
  const isDisclaimerMissing =
    !disclaimerKey && !isLoadingAvailablePaymentMethods;

  useEffect(() => {
    if (isDisclaimerMissing) {
      console.warn(
        "Hiding the card disclaimer, no rule applies to card",
        card.id
      );
    }
  }, [isDisclaimerMissing, card.id]);

  return disclaimerKey ? t(disclaimerKey, { client: clientName }) : null;
};
